import React from "react";
import ReactDatePickerModule from "react-datepicker";
import { id as idn } from "date-fns/locale";
import { getMonth, getYear } from "date-fns";
import { twMerge } from "tailwind-merge";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { isEmpty } from "../helpers";

// Handle interop CJS/ESM (Rolldown)
const ReactDatePicker = ReactDatePickerModule.default ?? ReactDatePickerModule;

const DatePicker = ({
  dateFormat = "dd/MM/yyyy",
  name,
  selected = null,
  className,
  errors,
  errorCustomId,
  label,
  disabled,
  required = false,
  onChange,
  isClearable = false,
  minDate = null,
  maxDate = null,
  includeDateIntervals = null,
}) => {
  const months = [
    "Januari",
    "Februari",
    "Maret",
    "April",
    "Mei",
    "Juni",
    "Juli",
    "Agustus",
    "September",
    "Oktober",
    "November",
    "Desember",
  ];
  const CustomHeader = (
    {
      date,
      changeYear,
      changeMonth,
      decreaseMonth,
      increaseMonth,
      prevMonthButtonDisabled,
      nextMonthButtonDisabled,
    },
    name,
  ) => {
    return (
      <div className="flex gap-1 items-center w-full justify-center">
        <button
          type="button"
          formNoValidate
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
        >
          <ChevronLeft size={20} />
        </button>
        <select
          id={`month-${name}`}
          value={months[getMonth(date)]}
          onChange={({ target: { value } }) => changeMonth(months.indexOf(value))}
          className="text-sm bg-transparent w-20 ml-6 inline-block"
        >
          {months.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <input
          id={`year-${name}`}
          type="number"
          className="text-sm bg-transparent w-14"
          value={getYear(date)}
          onChange={({ target: { value } }) => changeYear(value)}
          min={1950}
          max={getYear(new Date()) + 30}
        />
        <button
          type="button"
          formNoValidate
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
        >
          <ChevronRight size={20} />
        </button>
      </div>
    );
  };

  return (
    <div className="input-group-date relative">
      <ReactDatePicker
        locale={idn}
        id={name}
        renderCustomHeader={(e) => CustomHeader(e, name)}
        wrapperClassName="w-full"
        dateFormat={dateFormat}
        className={twMerge(
          "input-date",
          className,
          selected ? "has-value" : "",
          errors?.[name] || errorCustomId
            ? "border-[1.5px] border-red-500"
            : "border-[1.5px] border-gray-300",
          disabled ? "bg-gray-200" : "bg-transparent",
        )}
        selected={
          selected && typeof selected === "string"
            ? new Date(selected)
            : selected instanceof Date
              ? selected
              : null
        }
        autoComplete="off"
        placeholderText="dd/mm/yyyy"
        onChange={onChange}
        disabled={disabled}
        isClearable={isClearable}
        minDate={minDate}
        maxDate={maxDate}
        includeDateIntervals={includeDateIntervals}
      />
      <label
        className={twMerge(
          `input-date-label pointer-events-none`,
          errors?.[name] || errorCustomId ? "text-rose-500" : "text-zinc-700",
          disabled
            ? !isEmpty(selected)
              ? "bg-linear-to-b from-white from-50% to-gray-200 to-50%"
              : "bg-transparent"
            : "bg-white",
        )}
        htmlFor={name}
      >
        {label || ""}
        {required && <span className="text-rose-500 ml-1">*</span>}
      </label>
      {(errors?.[name] || errorCustomId) && (
        <p className="text-rose-500 text-sm mt-2">
          {errors?.[name]?.message || errorCustomId?.message || ""}
        </p>
      )}
    </div>
  );
};

export default DatePicker;
